import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, Button, TintableIcon } from '@/components/ui';
import { NotificationBell } from '@/components/features/Notifications/NotificationBell';
import { useAppUser } from '@/lib/useAppUser';
import { useAppAuth } from '@/lib/useAppAuth';
import { useNavigate } from 'react-router-dom';
import { useWithdrawalGetBalance } from '@/lib/endpoints';
import { useKycStatus } from '@/lib/kyc/useKyc';
import { useKycIdentityPending } from '@/lib/kyc/useKycIdentityPending';
import { dollarsToCents, formatCentsGrouped } from '@/lib/format-currency';
import { TopBarSkeleton } from './TopBarSkeleton';

import dollarSign from '@/assets/dollar-sign.svg';
import plusIcon from '@/assets/plus.svg';
import verifyBadge from '@/assets/settings/verify-badge.svg';

const TEST_ID_PREFIX = 'top-bar';

export interface TopBarProps {
  /** Additional CSS classes applied to the root element. */
  className?: string;
  /**
   * Compact mode used when the home top bar is pinned to the viewport.
   * Hides the avatar (so the balance pill stretches to fill the row),
   * shrinks the balance pill height, and uses smaller typography.
   */
  compact?: boolean;
}

/**
 * Top navigation bar — avatar, cash-balance pill, and notification bell
 * (lobby V2, Figma 5751-141388 "Balance"). The Crown Dollars chip was
 * dropped from this bar in the V2 design.
 *
 * Sits at the very top of the lobby screen and gives users quick access
 * to their balance and notifications.
 *
 * @example
 * ```tsx
 * <TopBar />
 * ```
 */
export function TopBar({ className, compact = false }: TopBarProps) {
  const user = useAppUser();
  const { isAuthenticated } = useAppAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // On the settings page the avatar slot becomes a back button — the avatar
  // is what navigated here, so it would be a dead self-link.
  const onSettings = pathname === '/settings';

  // KYC verify badge on the avatar — same gating as SideNav/AccountHeader:
  // identity KYC satisfied once /kyc/status has loaded.
  const { data: kyc } = useKycStatus(isAuthenticated);
  const pendingIdentity = useKycIdentityPending();
  const isVerified = Boolean(kyc) && pendingIdentity === null;

  // Real wallet cash balance (GET /withdrawals/balance). Kept live by
  // useWalletBalanceRealtime, which patches/invalidates this same query key on
  // the backend `balance:changed` socket push.
  // regen: api B3 dropped `lockedBalance` from this DTO — the bar only renders
  // `balance`, so no change here; the Orval model loses the field on next regen.
  const { data, isLoading } = useWithdrawalGetBalance({ query: { enabled: !!user } });

  if (isLoading) return <TopBarSkeleton />;

  return (
    <div
      data-testid={TEST_ID_PREFIX}
      className={cn(
        'flex items-center gap-[9px] transition-[height] duration-150',
        compact ? 'h-10' : 'h-[50px]',
        className,
      )}
    >
      {/* User avatar — opens settings; on /settings it becomes a back button (hidden in compact mode) */}
      {!compact &&
        (onSettings ? (
          <button
            type="button"
            data-testid={`${TEST_ID_PREFIX}-back`}
            aria-label="Go back"
            // Deep links have no in-app history entry to pop — fall back home.
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/'))}
            className="flex size-[46px] shrink-0 items-center justify-center rounded-full bg-surface border-2 border-solid border-input-soft"
          >
            <ChevronLeft aria-hidden="true" className="size-6 text-purple" strokeWidth={3} />
          </button>
        ) : (
          <Link
            data-testid={`${TEST_ID_PREFIX}-profile`}
            to="/settings"
            aria-label="Open settings"
            className="relative rounded-full"
          >
            <Avatar
              src={user?.profileImage}
              className="size-[46px] border-2 border-solid border-input-soft bg-surface shadow-none"
              size="lg"
              alt="User avatar"
            />
            {isVerified && <img src={verifyBadge} alt="Verified account" className="absolute -right-0.5 bottom-0 size-4" />}
          </Link>
        ))}

      {/* Coin balance display */}
      <div
        data-testid={`${TEST_ID_PREFIX}-balance`}
        className={cn(
          'flex items-center gap-2.5 bg-surface border-2 border-input-soft rounded-[47px] flex-1 transition-[height,padding] duration-150',
          compact ? 'h-10 px-3 py-1' : 'h-[50px] px-5 py-1.5',
        )}
      >
        {/* Masked over currentColor so the glyph follows the theme (the raw
            svg has a near-black fill baked in, which vanishes in dark mode). */}
        <TintableIcon src={dollarSign} className={cn('text-ink', compact ? 'size-4' : 'size-5')} />
        <span
          data-testid={`${TEST_ID_PREFIX}-balance-value`}
          className={cn(
            'flex-1 font-normal text-ink tracking-[-0.4px] transition-[font-size] duration-150',
            compact ? 'text-base' : 'text-xl',
          )}
        >
          {/* Two decimals always ("0.00", "1,234.50") — the pill's $ icon carries the symbol. */}
          {data?.balance != null ? formatCentsGrouped(dollarsToCents(data.balance)) : ''}
        </span>
        <Button
          data-testid={`${TEST_ID_PREFIX}-add-coins-button`}
          variant="ghost"
          size="icon"
          className={compact ? 'size-8' : 'size-[37px]'}
          aria-label="Add coins"
          onClick={() => navigate('/wallet')}
        >
          <img src={plusIcon} alt="Plus icon" className={cn('shrink-0', compact ? 'size-5' : 'size-[22px]')} />
        </Button>
      </div>

      {/* Notifications bell — single mount serves both lobby and account page */}
      <NotificationBell compact={compact} />
    </div>
  );
}
